// @flow

import * as React from 'react';

import { type DateLike } from 'app/lib/date';
import LegacyDispatcher from "app/lib/legacyDispatcher";

import GitCommitNotFoundWarning from './GitCommitNotFoundWarning';
import JobLogOutput from './Output';
import JobUsedAgentNoPluginsWarning from './JobUsedAgentNoPluginsWarning';
import SSHKeysWarning from './SSHKeysWarning';

import UserSessionStore from 'app/stores/UserSessionStore';

import Emojify from 'app/components/shared/Emojify';
import ScrollFollower from 'app/components/shared/ScrollFollower';

import { type Job } from 'app/stores/JobLogStore';

const USER_SESSION_STORE_KEY = "job-log-timestamps-on";

type Props = {
  parentElementId: string,
  job: {
    id: string,
    basePath: string,
    state: string,
    passed: boolean,
    exitStatus: number,
    logDeletedMessage: string,
    softFailed: boolean,
    projectProviderId: string,
    startedAt: DateLike,
    agentOsId: string,
    agentName: string,
    usingPlugins: boolean,
    agentVersion: string,
    permissions: {
      deleteJobLog: {
        allowed: boolean
      }
    }
  },
  build: {
    branchName: string,
    commitId: string
  }
};

type State = {
  following: boolean,
  sshKeyFailure?: boolean,
  gitCommitNotFoundInBranchFailure?: boolean,
  linesHaveTimestamps?: boolean,
  timestampsOn?: boolean
};

export default class JobLog extends React.PureComponent<Props, State> {
  state = {
    following: false
  };

  isTimestampsOn() {
    // If timestamps aren't even available in this log, don't bother doing anythingf else
    if (!this.state.linesHaveTimestamps) {
      return false;
    }

    // If timestmaps haven't been manually turned on in this log, use what ever
    // they chose last.
    if (this.state.timestampsOn === undefined) {
      // Values are stored as strings, so we'll need to convert it
      return UserSessionStore.get(USER_SESSION_STORE_KEY) === "true";
    }

    return this.state.timestampsOn;
  }

  render() {
    if (this.props.job.logDeletedMessage) {
      return this.renderDeletedLog();
    }

    return this.renderLog();
  }

  renderDeletedLog() {
    return (
      <div className="JobLogOutputComponent p0" style={{ minHeight: "0" }}>
        <div className="JobLogOutputComponent__Body py3 px2">
          <Emojify text={this.props.job.logDeletedMessage} />
        </div>
      </div>
    );
  }

  renderExitStatusMessage() {
    if (this.props.job.exitStatus === -1) {
      return `Exited with status ${this.props.job.exitStatus} (process killed or agent lost)`;
    }

    if (this.props.job.softFailed) {
      return `Exited with status ${this.props.job.exitStatus} (soft failed)`;
    }

    return `Exited with status ${this.props.job.exitStatus}`;
  }

  renderLog() {
    const exitStatusNode = (
      this.props.job.exitStatus != null
        ? (
          <div className="JobLogComponent__ExitStatus">
            <span>
              {this.renderExitStatusMessage()}
            </span>
          </div>
        )
        : null
    );

    const sshKeyFailureNode = this.state.sshKeyFailure && (
      <SSHKeysWarning
        repositoryProviderId={this.props.job.projectProviderId}
        osId={this.props.job.agentOsId}
      />
    );

    const gitCommitNotFoundInBranchFailureNode = this.state.gitCommitNotFoundInBranchFailure && (
      <GitCommitNotFoundWarning
        branch={this.props.build.branchName}
        commit={this.props.build.commitId}
      />
    );

    // Show a message about plugins if the current job is using them, but it ran
    // on an unsupported agent.
    const pluginsNotSupportedNode = this.props.job.usingPlugins && this.props.job.agentVersion && !this.props.job.agentVersion.match(/^3\./) && (
      <JobUsedAgentNoPluginsWarning
        job={this.props.job}
      />
    );

    const followLogButtonNode = (
      this.state.following
        ? (
          <a
            href="#"
            className="JobLogOutputComponent__Button--Active pull-right"
            onClick={this.handleClickFollowLog}
          >
            <i className="fa fa-arrow-down" />
            Following…
          </a>
        )
        : (
          <a
            href="#"
            className="JobLogOutputComponent__Button pull-right"
            onClick={this.handleClickFollowLog}
          >
            <i className="fa fa-arrow-down" />
            Follow
          </a>
        )
    );

    const deleteLogButtonNode = (
      ((this.props.job.state === 'finished') || (this.props.job.state === 'canceled')) && this.props.job.permissions.deleteJobLog.allowed && (
        <a
          href={this.props.job.basePath + "/delete_log"}
          className="JobLogOutputComponent__Button pull-right"
          data-method="delete"
          data-confirm="Permanently delete this job’s log?"
        >
          <i className="fa fa-trash" />
          Delete
        </a>
      )
    );

    const isTimestampsOn = this.isTimestampsOn();

    const toggleTimestampsButtonNode = (
      this.state.linesHaveTimestamps &&
      <a
        href="#"
        className="JobLogOutputComponent__Button"
        onClick={this.handleClickToggleTimestamps}
      >
        <i className="fa fa-calendar-o" />
        {isTimestampsOn ? "Hide timestamps" : "Show timestamps"}
      </a>
    );

    return (
      <div data-testid="JobLogComponent" className="JobLogComponent">
        {sshKeyFailureNode}
        {gitCommitNotFoundInBranchFailureNode}
        {pluginsNotSupportedNode}
        <ScrollFollower
          following={this.state.following}
          onManualScroll={this.handleManualScroll}
          el={this.props.parentElementId}
        >
          <JobLogOutput
            job={this.props.job}
            onChange={this.handleJobLogChange}
            timestampsOn={isTimestampsOn}
          >
            <a
              href="#"
              className="JobLogOutputComponent__Button"
              onClick={this.handleClickExpandGroups}
            >
              <i className="fa fa-plus" />
              Expand groups
            </a>
            <a
              href="#"
              className="JobLogOutputComponent__Button"
              onClick={this.handleClickCollapseLogs}
            >
              <i className="fa fa-minus" />
              Collapse groups
            </a>
            {toggleTimestampsButtonNode}
            {followLogButtonNode}
            <a
              href={this.props.job.basePath + "/download.txt"}
              className="JobLogOutputComponent__Button pull-right"
            >
              <i className="fa fa-download" />
              Download
            </a>
            {deleteLogButtonNode}
          </JobLogOutput>
          <div className="JobLogComponent__Footer pt2" style={{ fontSize: 13 }}>
            {exitStatusNode}
            <div className="JobLogComponent__BackToTop">
              <a
                href={`#${this.props.parentElementId}`}
                className="JobLogComponent__BackToTop__Button"
              >
                <i className="fa fa-arrow-up" />
                Back to Job
              </a>
            </div>
          </div>
        </ScrollFollower>
      </div>
    );
  }

  handleJobLogChange = (log: Job) => {
    this.setState({
      sshKeyFailure: (log.sshKeyFailure && (this.props.job.state === 'finished') && !this.props.job.passed),
      gitCommitNotFoundInBranchFailure: (log.gitCommitNotFoundInBranchFailure && (this.props.job.state === 'finished') && !this.props.job.passed),
      linesHaveTimestamps: log.linesHaveTimestamps
    });
  }

  handleManualScroll = () => {
    this.setState({ following: false });
  }

  handleClickFollowLog = (evt: SyntheticMouseEvent<HTMLAnchorElement>) => {
    evt.preventDefault();

    this.setState({ following: !this.state.following });
  }

  handleClickExpandGroups = (evt: SyntheticMouseEvent<HTMLAnchorElement>) => {
    evt.preventDefault();

    LegacyDispatcher.emit("job_log_group:expand_all", {
      jobId: this.props.job.id
    });
  }

  handleClickCollapseLogs = (evt: SyntheticMouseEvent<HTMLAnchorElement>) => {
    evt.preventDefault();

    LegacyDispatcher.emit("job_log_group:collapse_all", {
      jobId: this.props.job.id
    });
  }

  handleClickToggleTimestamps = (evt: SyntheticMouseEvent<HTMLAnchorElement>) => {
    evt.preventDefault();

    const timestampsOn = !this.isTimestampsOn();

    this.setState({ timestampsOn: timestampsOn });

    // Store it so next time we default to that choice
    UserSessionStore.set(USER_SESSION_STORE_KEY, timestampsOn.toString());
  }
}
